import { useCallback } from 'react'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import {
    Form,
    ImageStorageResourceSelect,
    TimezoneSelect,
    useForm,
    useObservable,
    useUpload,
} from '@campai/lib-web/components'
import {
    Button,
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
    Field,
    FieldError,
    FieldGroup,
    FieldLabel,
    Input,
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Textarea,
    showDialog,
} from '@campai/lib-web/components/new/ui'
import { client } from '@campai/lib-web/utils/trpc'
import { getDefaultTimezone } from '@campai/lib-web/utils/timezone'
import { env } from '@campai/lib-web/env'
import {
    Language,
    LanguageSchema,
    UserSchema,
    createInputStorageResource,
    extractStorageResource,
} from '@campai/lib-common/entities'
import { getMinMaxLength } from '@campai/lib-common/utils/zod'

import { appState } from '@/appState'

import { EmailChangeDialog } from './EmailChangeDialog'

export const Personal = observer(() => {
    const { t } = useTranslation()
    const { formProps, formRef, formResult, isSubmittingForm } = useForm({ toastMode: 'both' })
    const { upload, isUploading } = useUpload()

    const user = appState.user
    const canChangeEmail =
        env.VITE_AUTH_PROVIDER === 'authy' || (user.authProviders.length === 1 && user.authProviders[0] === 'blueid')

    const userProfile = useObservable(() => ({
        name: user.name,
        email: user.email,
        avatar: extractStorageResource(user.avatar),
        language: user.language,
        timezone: user.timezone ?? getDefaultTimezone(),
        bio: user.bio,
    }))

    const handleSubmit = useCallback(async () => {
        await client.currentUser.updateUserProfile.mutate({
            ...userProfile,
            avatar: createInputStorageResource(userProfile.avatar) || null,
        })

        await appState.loadUser(true) // update cached information
    }, [userProfile])

    const handleChangeEmail = useCallback(
        (newEmail: string) => {
            runInAction(() => {
                userProfile.email = newEmail
                appState.user = {
                    ...appState.user,
                    email: newEmail,
                }
            })
        },
        [userProfile]
    )

    const i18nKey = 'profile.personal'

    return (
        <Card>
            <CardHeader className="border-b">
                <CardTitle>{t(`${i18nKey}.title`)}</CardTitle>
                <CardDescription>{t(`${i18nKey}.description`)}</CardDescription>
            </CardHeader>
            <CardContent>
                <Form {...formProps} onSubmit={handleSubmit}>
                    <FieldGroup>
                        <Field>
                            <ImageStorageResourceSelect
                                error={formResult?.validation?.getError('avatar')}
                                aspectRatio="1/1"
                                imageWidthClassName="w-24"
                                maxSize={512}
                                shape="circle"
                                allowClear
                                value={userProfile.avatar}
                                onChange={(value) =>
                                    runInAction(() => {
                                        userProfile.avatar = value
                                    })
                                }
                                upload={upload}
                            />
                        </Field>
                        <div className="grid gap-4 md:grid-cols-2">
                            <Field>
                                <FieldLabel htmlFor="user-profile-name" required>
                                    {t(`${i18nKey}.name`)}
                                </FieldLabel>
                                <Input
                                    id="user-profile-name"
                                    required
                                    {...getMinMaxLength(UserSchema.shape.name, true)}
                                    aria-invalid={!!formResult?.validation?.getError('name')}
                                    value={userProfile.name}
                                    onChange={(event) =>
                                        runInAction(() => {
                                            userProfile.name = event.target.value
                                        })
                                    }
                                />
                                <FieldError>{formResult?.validation?.getError('name')}</FieldError>
                            </Field>
                            <Field>
                                <FieldLabel htmlFor="user-profile-email" required>
                                    {t(`${i18nKey}.email`)}
                                </FieldLabel>
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                                    <Input
                                        id="user-profile-email"
                                        type="email"
                                        required
                                        disabled
                                        className="flex-1"
                                        value={userProfile.email}
                                    />
                                    {canChangeEmail && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={() =>
                                                void showDialog(({ close }) => (
                                                    <EmailChangeDialog close={close} onUpdate={handleChangeEmail} />
                                                ))
                                            }
                                        >
                                            {t(`${i18nKey}.changeEmail`)}
                                        </Button>
                                    )}
                                </div>
                            </Field>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Field>
                                <FieldLabel htmlFor="user-profile-language" required>
                                    {t(`${i18nKey}.language`)}
                                </FieldLabel>
                                <Select
                                    value={userProfile.language}
                                    onValueChange={(value) =>
                                        runInAction(() => {
                                            userProfile.language = value as Language
                                        })
                                    }
                                >
                                    <SelectTrigger
                                        id="user-profile-language"
                                        className="w-full"
                                        aria-invalid={!!formResult?.validation?.getError('language')}
                                    >
                                        <SelectValue>{t(`language.${userProfile.language}`)}</SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectGroup>
                                            {(Object.keys(LanguageSchema.enum) as Array<Language>).map((language) => (
                                                <SelectItem key={language} value={language}>
                                                    {t(`language.${language}`)}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                    </SelectContent>
                                </Select>
                                <FieldError>{formResult?.validation?.getError('language')}</FieldError>
                            </Field>
                            <Field>
                                <FieldLabel htmlFor="user-profile-timezone" required>
                                    {t(`${i18nKey}.timezone`)}
                                </FieldLabel>
                                <TimezoneSelect
                                    id="user-profile-timezone"
                                    aria-invalid={!!formResult?.validation?.getError('timezone')}
                                    className="w-full"
                                    required
                                    value={userProfile.timezone}
                                    onChange={(value) => {
                                        runInAction(() => {
                                            userProfile.timezone = value
                                        })
                                    }}
                                />
                                <FieldError>{formResult?.validation?.getError('timezone')}</FieldError>
                            </Field>
                        </div>
                        <Field>
                            <FieldLabel htmlFor="user-profile-bio">{t(`${i18nKey}.bio`)}</FieldLabel>
                            <Textarea
                                id="user-profile-bio"
                                rows={8}
                                {...getMinMaxLength(UserSchema.shape.bio)}
                                aria-invalid={!!formResult?.validation?.getError('bio')}
                                value={userProfile.bio || ''}
                                onChange={(event) =>
                                    runInAction(() => {
                                        userProfile.bio = event.target.value
                                    })
                                }
                            />
                            <FieldError>{formResult?.validation?.getError('bio')}</FieldError>
                        </Field>
                    </FieldGroup>
                </Form>
            </CardContent>
            <CardFooter className="justify-end">
                <Button
                    onClick={() => formRef.current?.submit()}
                    disabled={isUploading}
                    loading={isSubmittingForm || isUploading}
                >
                    {t('actions.saveChanges')}
                </Button>
            </CardFooter>
        </Card>
    )
})
